/**
 * Analytics event surface for StationCard. PLACEHOLDER until the
 * Mammoth Analytics web SDK / AppMetrica web SDK is integrated —
 * `reportStationCardEvent` calls the supplied `Reporter` synchronously
 * but no real network transport exists yet.
 *
 * Event names below MUST match the canonical V2 keys also emitted by
 * iOS `MetroModules/Sources/StationCard/Analytics/StationCardAnalytics.swift`
 * so a single dashboard works for both platforms once the web SDK
 * lands. The V2 names are:
 *   station_card_opened          { station_id, transfers_count }
 *   station_card_settled         { station_id }
 *   station_card_from_tapped     { station_id }
 *   station_card_to_tapped       { station_id }
 *   station_card_details_tapped  { station_id }
 *   station_card_transfer_tapped { from_id, to_id }
 *   station_card_closed          { station_id }
 *
 * `detailsTapped` was added during the 2026-04-30 design parity sprint
 * to complete the action-row coverage (From / To were already wired).
 */
export type StationCardEvent =
  | { kind: 'opened'; stationId: string; transfersCount: number }
  | { kind: 'settled'; stationId: string }
  | { kind: 'fromTapped'; stationId: string }
  | { kind: 'toTapped'; stationId: string }
  | { kind: 'detailsTapped'; stationId: string }
  | { kind: 'transferTapped'; fromId: string; toId: string }
  | { kind: 'closed'; stationId: string }

interface AnalyticsEvent {
  name: string
  parameters: Record<string, string | number | boolean>
}

type Reporter = (event: AnalyticsEvent) => void

export function reportStationCardEvent(reporter: Reporter, event: StationCardEvent): void {
  switch (event.kind) {
    case 'opened':
      reporter({
        name: 'station_card_opened',
        parameters: { station_id: event.stationId, transfers_count: event.transfersCount },
      })
      return
    case 'settled':
      reporter({ name: 'station_card_settled', parameters: { station_id: event.stationId } })
      return
    case 'fromTapped':
      reporter({ name: 'station_card_from_tapped', parameters: { station_id: event.stationId } })
      return
    case 'toTapped':
      reporter({ name: 'station_card_to_tapped', parameters: { station_id: event.stationId } })
      return
    case 'detailsTapped':
      reporter({
        name: 'station_card_details_tapped',
        parameters: { station_id: event.stationId },
      })
      return
    case 'transferTapped':
      reporter({
        name: 'station_card_transfer_tapped',
        parameters: { from_id: event.fromId, to_id: event.toId },
      })
      return
    case 'closed':
      reporter({ name: 'station_card_closed', parameters: { station_id: event.stationId } })
      return
  }
}
